import { useEffect, useRef } from 'react';
import { useDispatch, useSelector, RootStateOrAny } from 'react-redux';

import { AuthActions, AuthState } from 'store/ducks/auth';

const THROTTLE_MS = 30_000; // Atualiza lastActivity no máximo 1x a cada 30s
const CHECK_INTERVAL_MS = 60_000; // Verifica inatividade a cada 60s
const INACTIVITY_LIMIT_MS = 3_600_000; // 1 hora
const LAST_ACTIVITY_STORAGE_KEY = 'evonik:last-activity';

const ACTIVITY_EVENTS: string[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click',
];

const ActivityMonitor: React.FC = () => {
  const dispatch = useDispatch();
  const { loggedIn, lastActivity } = useSelector<RootStateOrAny, AuthState>(
    (state) => state.auth
  );

  const lastThrottleRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number | null>(lastActivity);

  // Mantém a ref sincronizada com o valor do store
  useEffect(() => {
    lastActivityRef.current = lastActivity;
  }, [lastActivity]);

  useEffect(() => {
    if (!loggedIn) return;

    const persistLastActivity = (timestamp: number) => {
      localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(timestamp));
    };

    const updateLastActivity = (timestamp: number) => {
      dispatch(AuthActions.setLastActivity(timestamp));
      persistLastActivity(timestamp);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== LAST_ACTIVITY_STORAGE_KEY || !event.newValue) return;

      const nextTimestamp = Number(event.newValue);
      if (Number.isNaN(nextTimestamp)) return;

      const current = lastActivityRef.current ?? 0;
      if (nextTimestamp > current) {
        dispatch(AuthActions.setLastActivity(nextTimestamp));
      }
    };

    // Registra a atividade inicial ao logar
    const now = Date.now();
    const storedLastActivity = Number(
      localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY) ?? 0
    );
    const initialActivity = Number.isNaN(storedLastActivity)
      ? now
      : Math.max(now, storedLastActivity);

    updateLastActivity(initialActivity);
    lastThrottleRef.current = initialActivity;

    const handleActivity = () => {
      const currentTime = Date.now();
      if (currentTime - lastThrottleRef.current >= THROTTLE_MS) {
        lastThrottleRef.current = currentTime;
        updateLastActivity(currentTime);
      }
    };

    window.addEventListener('storage', handleStorage);

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity, { passive: true })
    );

    intervalRef.current = setInterval(() => {
      const last = lastActivityRef.current;
      if (last !== null && Date.now() - last > INACTIVITY_LIMIT_MS) {
        dispatch(AuthActions.logoutRequest());
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      window.removeEventListener('storage', handleStorage);

      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleActivity)
      );
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [loggedIn, dispatch]);

  return null;
};

export default ActivityMonitor;
